import { useEffect, useState } from 'react';
import appBranding from '@happylife-a/branding';
import webCore from '@happylife-a/web-core';
import { theme } from '../../../theme';
import { seoConfigs } from '../../../components';

const brandingUseCase = webCore.coreFactories.brandingUseCase;

function attribute(name) {
  if (typeof window === 'undefined') {
    return null;
  }
  return window?.document?.documentElement?.getAttribute(name);
}

function configureVersionsAndModes() {
  webCore.versioning.setVersions(
    import.meta.env.VITE_BUILD_VERSION_DATE,
    import.meta.env.VITE_BUILD_VERSION_GIT_LONG,
    import.meta.env.VITE_BUILD_VERSION_GIT_SHORT,
    import.meta.env.VITE_BUILD_VERSION_PKG,
    import.meta.env.VITE_CURRENT_BUILD_VERSION
  );

  webCore.environment.setEnvAndMode(
    import.meta.env.VITE_NODE_ENV || attribute('data-build-env'),
    import.meta.env.VITE_NODE_ENV_MODE || attribute('data-build-mode')
  );
}

function configureClientApi(clientName, tenantIdentifier) {
  let clientPlatform = 'SSR';
  let clientBuildVersion = 'SSR';

  if (typeof window !== 'undefined') {
    const buildVersion = attribute('data-build-version')?.replace(
      '{{CURRENT_BUILD_VERSION}}',
      'latest'
    );

    clientPlatform = { ua: window.navigator.userAgent };
    clientBuildVersion = buildVersion;
  }

  webCore.httpClient.setClientApiConfigs({
    clientName: clientName,
    clientBuildVersion: clientBuildVersion,
    clientPlatform: clientPlatform,
    apiTenantIdentifier: tenantIdentifier,
  });
}

async function loadAndSetConfigs(appType) {
  let possibleTenantIdentifier = document.documentElement.getAttribute('data-tenant-identifier');

  if (possibleTenantIdentifier === '{{BRANDING_APP_TENANT}}') {
    possibleTenantIdentifier = null;
  }

  const response = await brandingUseCase.getBrandingConfigs(possibleTenantIdentifier || null);

  const tenantIdentifier = response.tenantIdentifier;

  appBranding.configs.setDefaultTenantIdentifier(tenantIdentifier);
  appBranding.configs.setBrandingConfigs({
    environments: { [tenantIdentifier]: response.branding.environments },
    translations: { [tenantIdentifier]: response.branding.translations },
  });

  seoConfigs.setDomain(
    appBranding.configs.environments.getField(appBranding.fields.brand.Domain.Webapp)
  );

  theme.initWithBranding();

  configureVersionsAndModes();
  configureClientApi(appType, tenantIdentifier);
}

export default function useLoadTenantConfigs(appType) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    loadAndSetConfigs(appType)
      .then(() => setLoaded(true))
      .catch((e) => {
        const errorMessage =
          e?.response?.data?.message || 'Failed to load and initialize branding configurations.';

        alert(errorMessage);
      });
  }, []);

  return loaded;
}
